/**
 * DELETE JOB — client helper.
 *
 * ⚠️ Completely separate from the Cancel Job / Abort workflow (`cancel-job`).
 * Deleting a job never creates an abort, abort charge, invoice, or customer
 * cancellation email. This helper only calls the dedicated server endpoint;
 * it never deletes rows directly and never queues work while offline.
 */

import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

export type DeleteJobCode =
  | "DELETED"
  | "JOB_NOT_FOUND"
  | "COMPANY_FORBIDDEN"
  | "INVOICE_EXISTS"
  | "ACCOUNTING_SYNCED"
  | "OFFLINE"
  | "SERVER_ERROR";

export interface DeleteJobOutcome {
  success: boolean;
  code: DeleteJobCode;
  message: string;
}

export const DELETE_JOB_OFFLINE_MESSAGE = "Delete Job requires an internet connection.";

/**
 * Permanently delete a single job via the server workflow.
 * Destructive operations are NEVER queued for later execution.
 */
export async function deleteJobPermanently(
  jobId: string,
  reason?: string,
): Promise<DeleteJobOutcome> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { success: false, code: "OFFLINE", message: DELETE_JOB_OFFLINE_MESSAGE };
  }

  const { data, error } = await invokeEdgeFunction<{
    success?: boolean;
    code?: DeleteJobCode;
    error?: string;
    results?: { code?: DeleteJobCode; error?: string }[];
  }>("delete-job-permanently", { job_id: jobId, reason: reason || null });

  if (data?.success) {
    return { success: true, code: "DELETED", message: "Job deleted" };
  }

  const code = (data?.code || data?.results?.[0]?.code || "SERVER_ERROR") as DeleteJobCode;
  const message =
    data?.error ||
    data?.results?.[0]?.error ||
    error?.message ||
    "Unable to delete this job. Please try again.";

  // A second delete of an already-deleted job is a controlled, safe outcome.
  if (code === "JOB_NOT_FOUND") {
    return { success: false, code, message: "This job no longer exists." };
  }

  return { success: false, code, message };
}
